// src/manager/pages/TimeRTPSchedule.tsx — Manager Time-Based RTP Scheduler
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useManager } from '../hooks/useManager';
import { fetchCsrfToken } from '../../shared/api';
import { Loader2, Plus, Trash2 } from 'lucide-react';

interface ScheduleSlot { id: string; hour_start: number; hour_end: number; rtp_value: number; label: string; }
interface TableRow { id: string; name: string; }

const RTP_MIN_PERCENT = 50;
const RTP_MAX_PERCENT = 150;

export default function TimeRTPSchedule() {
  const { fetchTables } = useManager();
  const token = localStorage.getItem('managerToken');
  const [tables, setTables] = useState<TableRow[]>([]);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [schedule, setSchedule] = useState<ScheduleSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ hour_start: 18, hour_end: 23, rtp_value: 96, label: 'Peak Hours' });

  useEffect(() => {
    if (token) fetchTables().then(d => setTables((Array.isArray(d) ? d : []) as any[])).catch(() => {});
  }, [token, fetchTables]);

  const loadSchedule = useCallback(async (tableId: string) => {
    if (!tableId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`/manager/tables/${tableId}/rtp-schedule`,
        { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
      const data = await res.json();
      setSchedule(data.schedule || []);
    } finally { setLoading(false); }
  }, [token]);

const getCsrf = async () => {
  try { return await fetchCsrfToken(); }
  catch { return ''; }
};

  const saveSlot = async () => {
    if (!selectedTableId) { toast.error('Select a table'); return; }
    if (form.hour_start >= form.hour_end) { toast.error('End must be after start'); return; }
    if (form.rtp_value < RTP_MIN_PERCENT || form.rtp_value > RTP_MAX_PERCENT) {
      toast.error(`RTP must be ${RTP_MIN_PERCENT}-${RTP_MAX_PERCENT}%`);
      return;
    }
    try {
      const csrf = await getCsrf();
      const res = await fetch(`/manager/tables/${selectedTableId}/rtp-schedule`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-CSRF-Token': csrf },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error((await res.json()).error?.message);
      toast.success('Slot saved!');
      loadSchedule(selectedTableId);
    } catch (err: any) { toast.error(err.message); }
  };

  const deleteSlot = async (hour_start: number) => {
    try {
      const csrf = await getCsrf();
      await fetch(`/manager/tables/${selectedTableId}/rtp-schedule/${hour_start}`, {
        method: 'DELETE', credentials: 'include',
        headers: { Authorization: `Bearer ${token}`, 'X-CSRF-Token': csrf }
      });
      toast.success('Deleted'); loadSchedule(selectedTableId);
    } catch (err: any) { toast.error(err.message); }
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const getSlot = (h: number) => schedule.find(s => h >= s.hour_start && h < s.hour_end);
  const now = new Date().getHours();

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Time-Based RTP</h2>
        <p className="text-gray-500 mt-1">Automatically change RTP at different times of day for your tables (50-150%)</p>
      </div>

      {/* Table Selector */}
      <div className="bg-white rounded-xl shadow p-5 border border-gray-200">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Select Table</label>
        <select value={selectedTableId}
          onChange={e => { setSelectedTableId(e.target.value); if (e.target.value) loadSchedule(e.target.value); }}
          className="w-full md:w-64 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 text-sm">
          <option value="">— Choose table —</option>
          {tables.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {selectedTableId && (
        <>
          {/* Timeline */}
          <div className="bg-white rounded-xl shadow p-5 border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4">24-Hour Timeline</h3>
            <div className="flex gap-0.5 h-14 rounded-lg overflow-hidden border border-gray-200">
              {hours.map(h => {
                const slot = getSlot(h);
                const isNow = h === now;
                return (
                  <div key={h}
                    className={`flex-1 flex flex-col items-center justify-center text-center relative
                      ${slot ? (slot.rtp_value >= 95 ? 'bg-green-400' : slot.rtp_value >= 88 ? 'bg-yellow-300' : 'bg-red-300') : 'bg-gray-100'}
                      ${isNow ? 'ring-2 ring-purple-500 ring-inset' : ''}`}
                    title={slot ? `${h}:00 → ${slot.rtp_value}% (${slot.label})` : `${h}:00 — Base RTP`}>
                    {slot && <span className="font-bold text-gray-800" style={{ fontSize: '8px' }}>{slot.rtp_value}%</span>}
                    {h % 6 === 0 && <span className="text-gray-600" style={{ fontSize: '7px' }}>{h}h</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Add Slot */}
          <div className="bg-white rounded-xl shadow p-5 border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Plus size={18} /> Add Time Slot</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Start Hour (0-23)', key: 'hour_start', min: 0, max: 23 },
                { label: 'End Hour (1-24)', key: 'hour_end', min: 1, max: 24 },
                { label: 'RTP % (50-150)', key: 'rtp_value', min: RTP_MIN_PERCENT, max: RTP_MAX_PERCENT },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                  <input type="number" min={f.min} max={f.max} value={(form as any)[f.key]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: +e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                <input type="text" value={form.label} placeholder="Peak Hours"
                  onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500" />
              </div>
            </div>
            <button onClick={saveSlot}
              className="mt-4 px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm">
              Save Slot
            </button>
          </div>

          {/* Existing Schedule */}
          <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex justify-between">
              <h3 className="font-semibold text-gray-800">Active Schedule</h3>
              {loading && <Loader2 className="animate-spin text-purple-600" size={18} />}
            </div>
            {schedule.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No schedule — table uses base RTP setting</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                  <tr>
                    <th className="p-3 text-left">Time</th>
                    <th className="p-3 text-center">RTP</th>
                    <th className="p-3 text-left">Label</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map(s => {
                    const isActive = now >= s.hour_start && now < s.hour_end;
                    return (
                      <tr key={s.id} className={`border-b ${isActive ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                        <td className="p-3 font-mono font-medium">{s.hour_start}:00 – {s.hour_end}:00</td>
                        <td className="p-3 text-center font-bold text-purple-700">{s.rtp_value}%</td>
                        <td className="p-3 text-gray-600">{s.label || '—'}</td>
                        <td className="p-3 text-center">
                          {isActive
                            ? <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">ACTIVE</span>
                            : <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs">Scheduled</span>}
                        </td>
                        <td className="p-3 text-center">
                          <button onClick={() => deleteSlot(s.hour_start)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
